import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {OfferingFormCheckoutFormValues} from "../../../../components/offering-form/general/checkout-form/types";
import {subscriptionCheckoutFormValidationSchema} from "../../create-subscription/schema-validation";
import {useNavigate} from "react-router-dom";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {FormWrapper} from "../../../../components/form-wrapper";
import {SubscriptionCheckoutFormFields} from "../../../../components/offering-form/subscriptions/checkout-form";
import {OfferingDeleteButton} from "../../../../components/offering-form/general/delete-button";
import {useGetEditableSubscriptionContext} from "../context/get-editable-subscription";
import {useEditSubscriptionHook} from "../hooks/edit-subscription";
import {useDeleteSubscriptionFromEditHook} from "../hooks/delete-subscription";
import {mapApiBookingQuestionsToCheckoutForm} from "../map-subscription-to-form";
import type {UpdateSubscriptionApiPayload} from "../../../../react-query-hooks/store/subscriptions/update-subscription";

export function EditSubscriptionCheckoutForm() {
  const navigate = useNavigate();
  const {
    state: {subscriptionData},
  } = useGetEditableSubscriptionContext();
  const sub = subscriptionData.productSubscription;

  const checkoutFormMethods = useForm<OfferingFormCheckoutFormValues>({
    resolver: yupResolver(subscriptionCheckoutFormValidationSchema),
    mode: "onChange",
    defaultValues: {
      bookingQuestions: mapApiBookingQuestionsToCheckoutForm(sub.bookingQuestions),
    },
  });

  const {editSubscription, isEditingSubscription} = useEditSubscriptionHook();
  const {deleteSubscription} = useDeleteSubscriptionFromEditHook(sub.id);

  function onSubmit(values: OfferingFormCheckoutFormValues) {
    const bookingQuestions =
      values.bookingQuestions
        ?.map((q) => (typeof q === "string" ? q : q.question))
        .filter(Boolean) ?? [];

    const payload: UpdateSubscriptionApiPayload = {
      name: sub.name,
      description: sub.description,
      hideFromProfile: sub.hideFromProfile ?? false,
      requestPhoneNumber: sub.requestPhoneNumber ?? false,
      bookingQuestions,
    };
    const redirect = sub.redirectUrl;
    if (redirect && String(redirect).trim()) {
      payload.redirectUrl = String(redirect).trim();
    }

    editSubscription(sub.id, payload);
  }

  const isFormError = Object.keys(checkoutFormMethods.formState.errors).length > 0;
  const isBookingQuestionsError = checkoutFormMethods.formState.errors.bookingQuestions;

  return (
    <FormWrapper>
      <FormProvider {...checkoutFormMethods}>
        <form onSubmit={checkoutFormMethods.handleSubmit(onSubmit)} autoComplete="off">
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <SubscriptionCheckoutFormFields
              isBookingQuestionsError={!!isBookingQuestionsError}
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    className="secondary-btn"
                    onClick={() => navigate("/products?activeTab=subscriptions")}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    size="small"
                    disabled={
                      Object.keys(checkoutFormMethods.formState.errors).length > 0 ||
                      Object.values(checkoutFormMethods.watch()).length < 1
                    }
                    loading={isEditingSubscription}
                  >
                    Update subscription
                  </Button>
                  <OfferingDeleteButton
                    cancelText="Exit"
                    content="Are you sure you want to delete this subscription? This action cannot be undone."
                    okText="Delete subscription"
                    onOk={deleteSubscription}
                    title="Confirm subscription deletion"
                  />
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </FormWrapper>
  );
}
